import React, { FunctionComponent, useState, useEffect, useCallback } from 'react'
import classNames from 'classnames'

// import components
import { CommonButton, ConvertStringToReact, TrustArcLogo } from '@common/components'
import { connectRedux, formatDate, GoToComponentProps } from '@common/utils'
import { ConnectedModal } from '@common/connectedComponents/Modal'
// TODO: Changes needs to made based on unified config

import {
    BuyCustomizeActionsType,
    mapBuyCustomizeActions,
    mapBuyCustomizeState,
} from '@buy/connectedComponents/Customize/connector'
import { isAddressComplete } from '@buy/utils'
import { BuyCustomizeState } from '@buy/connectedComponents/Customize/state'
import { useCustomizeCart, useCustomizePageTracking } from '@buy/hooks'
import { BuyCardLabels } from '@buy/components/Card'
import { BuyFormData } from '@buy/state/buy-form/state'
import { BillingPeriod } from '@common/constants'
import { useParams } from 'react-router-dom'
import VolumeComponentsWrapper, { SKUSelectorsResponse } from '@buy/components/VolumeComponentWrapper'
import { AddonsWrapper } from '@buy/components/AddonsWrapper'
import { has, isEmpty } from 'lodash-es'
import { getComponentType } from '@buy/utils/volume-components-helpers'
import { FLEXIBLE_VOLUME_COMPONENT_TYPE } from '@buy/constants/flexible-components'
import { BasePlanSection } from '@buy/components/BasePlanSection/BasePlanSection'
import { ConnectedBuyOrderSummary } from '../OrderSummary'
import styles from './customize.module.css'
import billingStyles from '../../views/Billing/billing.module.css'

type ParamTypes = {
    plan: string
    pathProduct: string
}

type BuyCustomizeProps = GoToComponentProps<
    BuyCustomizeState,
    BuyCustomizeActionsType,
    {
        nextSteps: () => void
        isTabletOrMobileView?: boolean
        isMobileView?: boolean
    }
>

export interface Icons {
    [key: string]: string
}

const Customize: FunctionComponent<BuyCustomizeProps> = ({ state, actions, props }) => {
    const { plan: urlPlan } = useParams<ParamTypes>()
    const [initStateLoaded, setInitStateLoaded] = useState(false)
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false)
    const [volumeSelectorValues, setVolumeSelectorValues] = useState({})

    const { monthly, annual, user, year, month, billedMonthlyPrice, billedAnnualPrice, organizer, per } = state.content

    const cardLabels: BuyCardLabels = {
        monthly,
        annual,
        user,
        year,
        month,
        newLicenseCopy: '',
        highlightsLabel: '',
        billedMonthlyPrice,
        billedAnnualPrice,
        organizer,
        per,
    }

    const cart = useCustomizeCart({ ...state }, actions)

    useEffect(() => {
        if (state.selectedAddons.length > 0 && !initStateLoaded) {
            setInitStateLoaded(true)
        }
    }, [initStateLoaded, state.selectedAddons.length])

    const recalculatePrice = useCallback(
        (initialData: BuyFormData, planSKU?: string) => {
            if (isAddressComplete(initialData, state.statesOptional)) actions.calculatePrice(initialData, planSKU)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [actions]
    )

    useEffect(() => {
        recalculatePrice(state.initialData)
    }, [
        state.initialData,
        state.baseLine,
        state.additionalLines,
        recalculatePrice,
        state.addonLines,
        state.billingTenure,
    ])

    // tracking
    useCustomizePageTracking({
        dependencies: [state.baseLine.key, state.selectedPlan.name],
    })

    const submitBtnStyles = classNames({
        [billingStyles['buy-custom-submit-btn']]: true,
        [styles['customize-submit-btn']]: true,
    })

    const loggedInNoUpgrade = state.isUserLoggedIn && !cart.canUpgradePlan
    const v2NoDowngrade = urlPlan?.toLowerCase() === 'standard'

    useEffect(() => {
        const volumeSelectors = state.selectedPlan.sKUSelectors?.filter((item) => has(item, 'volumeSelector'))
        const basePlanSku: SKUSelectorsResponse = state.selectedPlan.sKUSelectors!.find((item) =>
            has(item, 'includedInSku')
        )!
        const tempVolumeSelectorValues: Record<string, string | number> = {}
        volumeSelectors?.forEach((item) => {
            const componentType = getComponentType(item.volumeSelector!)
            if (componentType === FLEXIBLE_VOLUME_COMPONENT_TYPE.DROPDOWN) {
                if (!isEmpty(basePlanSku?.unit) && !isEmpty(basePlanSku?.quantityMultiplier)) {
                    const defaultBundleQuantity = item.volumeSelector!.minimum! / Number(basePlanSku.quantityMultiplier)
                    tempVolumeSelectorValues[basePlanSku.unit.unitName] = defaultBundleQuantity.toString()
                }
                tempVolumeSelectorValues[item.unit.unitName] = item.volumeSelector?.minimum
                    ? item.volumeSelector?.minimum.toString()
                    : '1'
            }
            // default value , need to change when reconnecting sessionstorage
            else tempVolumeSelectorValues[item.unit.unitName] = 1 // default value , need to change when reconnecting sessionstorage
        })
        setVolumeSelectorValues({ ...tempVolumeSelectorValues })
    }, [state.selectedPlan.sKUSelectors])

    const updateVolumeSelectors = (unit: string, value: string | number) => {
        const basePlanSku: SKUSelectorsResponse = state.selectedPlan.sKUSelectors!.find((item) =>
            has(item, 'includedInSku')
        )!
        setVolumeSelectorValues((oldState: Record<string, string | number>) => {
            const newState: Record<string, string | number> = { ...oldState }
            newState[unit!] = value
            if (!isEmpty(basePlanSku?.unit) && !isEmpty(basePlanSku?.quantityMultiplier)) {
                const defaultBundleQuantity = Number(value) / Number(basePlanSku.quantityMultiplier)
                newState[basePlanSku.unit.unitName] = defaultBundleQuantity.toString()
            }
            return newState
        })
    }

    return (
        <div className="customize">
            <BasePlanSection
                skuSelectors={state.selectedPlan.sKUSelectors!}
                selectedPlanSku={state.selectedPlan.sKU!}
                shortName={state.selectedPlan.shortName}
                isMobileView={props.isMobileView}
                isAnnual={state.billingTenure.selected === BillingPeriod.ANNUAL}
                userLabel={state.content.user}
                yearLabel={state.content.year}
                monthLabel={state.content.month}
                loggedInNoUpgrade={loggedInNoUpgrade}
                baseLine={state.baseLine}
                billingTenure={state.billingTenure}
            />
            <VolumeComponentsWrapper
                skuSelectors={state.selectedPlan.sKUSelectors!}
                currencyCode={state.CurrencyCode}
                isYearlyBillingFrequency={state.billingTenure.selected === BillingPeriod.ANNUAL}
                closeLabel={state.content.upgradeClose}
                setBaseLine={actions.setBaseLine}
                setAdditionalLines={actions.setAdditionalLines}
                baseLine={state.baseLine}
                additionalLines={state.additionalLines}
                volumeSelectorValues={volumeSelectorValues}
                setVolumeSelectorValues={updateVolumeSelectors}
                billingPeriodKeys={state.billingPeriodKeys}
            />

            <AddonsWrapper
                skuSelectors={state.selectedPlan.sKUSelectors!}
                currencyCode={state.CurrencyCode}
                isYearlyBillingFrequency={state.billingTenure.selected === BillingPeriod.ANNUAL}
                labels={{
                    title: state.content.selectAddons,
                    description: state.content.selectAddonsDescription,
                    month: cardLabels.month,
                    year: cardLabels.year,
                    perLabel: state.content.per,
                }}
                locale={state.locale}
                accountSubscriptions={state.accountSubscriptions}
                addonLines={state.addonLines}
                setAddonLines={actions.setAddonLines}
                baseLine={state.baseLine}
                additionalLines={state.additionalLines}
                volumeSelectorValues={volumeSelectorValues}
                legacyAddons={state.selectedPlan.addons
                    ?.filter((item) => has(item, 'sKUSelector'))
                    .map((values) => values.sKUSelector)}
                billingTenure={state.billingTenure}
            />

            <ConnectedModal isOpen={isPlanModalOpen} closeable onClose={() => setIsPlanModalOpen(false)}>
                <div data-qat="change-plan-restriction-modal">
                    <h3>{state.content.changePlanMethodModalHeader}</h3>
                    <ConvertStringToReact htmlString={state.content.changePlanModalSubheader} />
                    <CommonButton onClick={() => setIsPlanModalOpen(false)}>
                        {state.content.modalCloseButtonText}
                    </CommonButton>
                </div>
            </ConnectedModal>
            {props.isTabletOrMobileView && <ConnectedBuyOrderSummary isTabletOrMobileView isMobileView />}
            <CommonButton
                type="button"
                modifier={submitBtnStyles}
                onClick={() => props.nextSteps()}
                disabled={
                    state.fatalErrorBlocker ||
                    state.totalLoading ||
                    (state.calculatedTotal === 0 && !cart.canUpgradePlan && !v2NoDowngrade)
                }
                data-qat="continue-to-checkout"
            >
                {state.content.continue}
            </CommonButton>
            {props.isTabletOrMobileView && <TrustArcLogo />}
            {state.isCouponValidAndApplied && state.couponServerData?.validTo && (
                <div className={styles.disclaimer} data-qat="disclaimer-message">
                    <ConvertStringToReact
                        htmlString={state.content.couponDisclaimer.replace(
                            '{couponExpDate}',
                            formatDate(new Date(state.couponServerData?.validTo), 'UTC', state.locale)
                        )}
                    />
                </div>
            )}
        </div>
    )
}

export const ConnectedBuyCustomize = connectRedux(Customize, mapBuyCustomizeState, mapBuyCustomizeActions)
